// La configuration de la coquille, déclarée par l'écran affiché.
//
// La coquille est montée une fois pour toutes au-dessus des écrans : elle ne
// se démonte plus quand on navigue, donc le rail déplié le reste et le
// panneau ouvert ne se referme pas tout seul. En échange, l'écran ne
// l'enveloppe plus : il déclare ce qu'elle doit montrer.
//
// Deux contextes plutôt qu'un : les écrans lisent la configuration, mais
// seule la coquille en a besoin, et les setters ne changent jamais. Un
// composant qui ne fait que poser sa configuration ne se rend donc pas à
// nouveau quand un autre pose la sienne.

import type {
  OngletCabinet,
  OngletDossier,
} from "@app/components/compta/ui/BandeOnglets";
import type { ComptaModule } from "@app/components/compta/ui/ComptaRail";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

interface PorteeCabinet {
  scope: "cabinet";
  onglet: OngletCabinet;
  pastillesOnglets?: Partial<Record<OngletCabinet, number>>;
}

interface PorteeDossier {
  scope: "dossier";
  onglet: OngletDossier;
  pastillesOnglets?: Partial<Record<OngletDossier, number>>;
}

// Les modules qui possèdent leur page entière (Clients, Échéancier…) : le
// haut ne porte que leur titre.
interface SansPortee {
  scope?: undefined;
  onglet?: undefined;
  pastillesOnglets?: undefined;
}

export type ConfigCoquille = {
  module: ComptaModule;
  // Le libellé du sélecteur en haut à gauche.
  titreScope: string;
} & (PorteeCabinet | PorteeDossier | SansPortee);

const ConfigContexte = createContext<ConfigCoquille | null>(null);

interface Setters {
  poser: (config: ConfigCoquille) => void;
  retirer: () => void;
}

const SettersContexte = createContext<Setters | null>(null);

interface FournisseurCoquilleProps {
  children: React.ReactNode;
}

export function FournisseurCoquille({ children }: FournisseurCoquilleProps) {
  const [config, setConfig] = useState<ConfigCoquille | null>(null);

  // Stables pour la vie de l'application : un écran qui pose sa
  // configuration ne fait pas se rendre à nouveau les autres.
  const setters = useMemo<Setters>(
    () => ({
      poser: (nouvelle) => setConfig(nouvelle),
      retirer: () => setConfig(null),
    }),
    []
  );

  return (
    <SettersContexte.Provider value={setters}>
      <ConfigContexte.Provider value={config}>
        {children}
      </ConfigContexte.Provider>
    </SettersContexte.Provider>
  );
}

// Lu par la coquille seule.
export function useConfigCoquille(): ConfigCoquille | null {
  return useContext(ConfigContexte);
}

/**
 * Appelé par un écran pour dire ce que la coquille doit montrer.
 *
 * Tout est comparable d'un rendu à l'autre : des chaînes, et pour les
 * pastilles un objet que l'écran mémorise (`useMemo`), sinon l'effet
 * repartirait à chaque rendu.
 */
export function useConfigurerCoquille(config: ConfigCoquille) {
  const setters = useContext(SettersContexte);
  if (!setters) {
    throw new Error(
      "useConfigurerCoquille doit être appelé sous un FournisseurCoquille"
    );
  }
  const { poser, retirer } = setters;

  const { module, titreScope, scope, onglet, pastillesOnglets } = config;

  useEffect(() => {
    poser({
      module,
      titreScope,
      scope,
      onglet,
      pastillesOnglets,
    } as ConfigCoquille);
    return retirer;
  }, [poser, retirer, module, titreScope, scope, onglet, pastillesOnglets]);
}
